// All user-facing copy lives here. Two locales (cs default, en) selected
// at boot via navigator.language + localStorage override; a Proxy makes
// `T.foo` always read from the active locale with cs as fallback so a
// missing key never throws.
//
// Tenant-supplied strings (sender_instructions, custom field labels) are
// NOT in this file — those come from the API and stay in the tenant's
// own language regardless of visitor locale.

const STRINGS = {
  cs: {
    loading: 'Načítám…',

    trustVerifiedLabel: 'Ověřená doména',
    trustUnverifiedLabel: 'Identita',
    trustUnverifiedValue: 'Neověřena doménou',
    moreInfoPrefix: 'Více informací: ',
    pausedNotice: 'Tento odkaz je dočasně pozastaven.',
    senderInstructionsLabel: 'Pokyny od příjemce:',

    pickFiles: 'Nahrát soubory',
    takePhoto: '📷 Vyfotit',
    pickFilesMobile: '📁 Nahrát soubory',
    dropzonePrimary: 'Přetáhněte soubory sem nebo klikněte pro výběr',
    dropzonePrimaryMobile: 'Klikněte pro výběr souborů',
    dropzoneHint: (mb) => `Maximální velikost ${mb} MB`,
    addMore: '+ Přidat soubory',
    uploadButton: 'Odeslat zašifrované',
    removeFile: '×',
    removeFileAria: 'Odstranit',
    totalSize: (mb) => `Celkem: ${mb} MB`,

    encrypting: 'Šifruji…',
    uploadingPercent: (pct) => `Nahrávám… ${pct}%`,

    doneTitle: 'Soubory bezpečně zašifrovány a odeslány.',
    doneRef: (id) => `Identifikátor: ${id}`,
    doneRetention: 'Soubory budou k dispozici 7 dní.',

    errLinkNotFound: 'Tento odkaz neexistuje nebo byl zrušen.',
    errLinkExpired: 'Platnost odkazu vypršela.',
    errPaused: 'Tento odkaz je dočasně pozastaven.',
    errNoPrekeys: 'Příjemce momentálně nemůže přijímat soubory. Zkuste to prosím později.',
    errTooLarge: (mb) => `Soubory přesahují limit ${mb} MB.`,
    errIntegrity: 'Klíč příjemce neprošel ověřením podpisu. Z bezpečnostních důvodů nelze nahrát.',
    errUploadFailed: 'Nahrávání se nezdařilo. Zkuste to prosím znovu.',
    errMissingToken: 'Neplatná adresa — chybí token.',

    retry: 'Zkusit znovu',

    senderInfoHeading: 'O odesílateli',
    senderInfoGdpr: 'Tato data budou zašifrována stejně jako vaše soubory a uvidí je pouze příjemce. Server GrataQ k nim nezískává přístup.',
    senderInfoFieldName: 'Jméno',
    senderInfoFieldSurname: 'Příjmení',
    senderInfoFieldEmail: 'E-mail',
    senderInfoFieldPhone: 'Telefon',
    senderInfoRequiredMark: '*',
    senderInfoMissingRequired: 'Vyplňte prosím všechna povinná pole (označená hvězdičkou).',
    senderInfoInvalidEmail: 'Zadejte e-mail ve tvaru jmeno@domena.cz.',

    formOnlyHint: 'Tento odkaz sbírá jen údaje z formuláře — žádné soubory se neodesílají.',
    formOnlySubmit: 'Odeslat formulář',
    formOnlyEmpty: 'Vyplňte prosím alespoň jedno pole.',
    formOnlyNoFields: 'Tento formulářový odkaz nemá žádná pole. Obraťte se na příjemce.',

    themeToggleAriaLight: 'Přepnout na světlý režim',
    themeToggleAriaDark: 'Přepnout na tmavý režim',
    langToggleAria: 'Změnit jazyk',
  },

  en: {
    loading: 'Loading…',

    trustVerifiedLabel: 'Verified domain',
    trustUnverifiedLabel: 'Identity',
    trustUnverifiedValue: 'Not domain-verified',
    moreInfoPrefix: 'More info: ',
    pausedNotice: 'This link is temporarily paused.',
    senderInstructionsLabel: 'Message from recipient:',

    pickFiles: 'Choose files',
    takePhoto: '📷 Take photo',
    pickFilesMobile: '📁 Choose files',
    dropzonePrimary: 'Drag files here or click to choose',
    dropzonePrimaryMobile: 'Tap to choose files',
    dropzoneHint: (mb) => `Maximum size ${mb} MB`,
    addMore: '+ Add more',
    uploadButton: 'Send encrypted',
    removeFile: '×',
    removeFileAria: 'Remove',
    totalSize: (mb) => `Total: ${mb} MB`,

    encrypting: 'Encrypting…',
    uploadingPercent: (pct) => `Uploading… ${pct}%`,

    doneTitle: 'Files securely encrypted and sent.',
    doneRef: (id) => `Reference: ${id}`,
    doneRetention: 'Files will be available for 7 days.',

    errLinkNotFound: 'This link does not exist or has been revoked.',
    errLinkExpired: 'This link has expired.',
    errPaused: 'This link is temporarily paused.',
    errNoPrekeys: 'The recipient cannot accept files right now. Please try again later.',
    errTooLarge: (mb) => `Files exceed the ${mb} MB limit.`,
    errIntegrity: "The recipient's key failed signature verification. Upload blocked for security.",
    errUploadFailed: 'Upload failed. Please try again.',
    errMissingToken: 'Invalid URL — token missing.',

    retry: 'Try again',

    senderInfoHeading: 'About sender',
    senderInfoGdpr: 'This data is encrypted just like your files and only the recipient can see it. The GrataQ server has no access.',
    senderInfoFieldName: 'First name',
    senderInfoFieldSurname: 'Last name',
    senderInfoFieldEmail: 'E-mail',
    senderInfoFieldPhone: 'Phone',
    senderInfoRequiredMark: '*',
    senderInfoMissingRequired: 'Please fill in all required fields (marked with an asterisk).',
    senderInfoInvalidEmail: 'Enter an e-mail like name@domain.com.',

    formOnlyHint: 'This link only collects form details — no files are sent.',
    formOnlySubmit: 'Submit form',
    formOnlyEmpty: 'Please fill in at least one field.',
    formOnlyNoFields: 'This form-only link has no fields. Please contact the recipient.',

    themeToggleAriaLight: 'Switch to light mode',
    themeToggleAriaDark: 'Switch to dark mode',
    langToggleAria: 'Change language',
  },
};

export const SUPPORTED = new Set(['cs', 'en']);

let LOCALE = 'en';

/**
 * Transparent locale lookup. `T.foo` returns the active-locale value,
 * falling back to cs when missing so a typo in en never throws.
 * Function values (`T.totalSize(5)`) work because the Proxy returns the
 * stored function reference unchanged.
 */
export const T = new Proxy({}, {
  get(_, key) {
    const active = STRINGS[LOCALE];
    if (active && Object.prototype.hasOwnProperty.call(active, key)) return active[key];
    return STRINGS.cs[key];
  },
});

export function setLocale(code) {
  LOCALE = SUPPORTED.has(code) ? code : 'en';
}

export function getLocale() {
  return LOCALE;
}

/**
 * Pick a locale at boot. localStorage override wins, then the browser
 * preference, then English as a final fallback (we expect more
 * international visitors than Czech-only ones).
 */
export function detectLocale() {
  try {
    const saved = localStorage.getItem('grataq-locale');
    if (saved && SUPPORTED.has(saved)) return saved;
  } catch { /* private mode etc. */ }
  const browser = (typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en')
    .slice(0, 2).toLowerCase();
  return SUPPORTED.has(browser) ? browser : 'en';
}

/** Persist a locale choice so reloads remember it. */
export function persistLocale(code) {
  try { localStorage.setItem('grataq-locale', code); } catch { /* private mode etc. */ }
}
